import { type WebLegacyWidgetEntity } from '@web/types';
import {
    Attachment,
    type ButtonSettings,
    CardKindViewType,
    type GallerySettings,
    type GeneralSettings,
    type InfoSettings,
    type ProductCardWidgetData,
    ViewModalType,
} from '@web/widgets/product-card/type/product-card-widget.type';
import type { JssStyle } from 'jss';
import { EButtonStyleType, EFontStyle, EHorizontalPosition, ESize, InputBorderEnum } from '@common/enums';
import { getBaseButtonData } from '@web/widgets/common/base/base-button/base-button.styles';

import { themeColorCss, themeFontCss } from '@sites/site-theme/src/utils/variables';
import { ThemeFontTags } from '@sites/site-theme/src/enums/font-tags.enum';
import { ThemeFontProperties } from '@sites/site-theme/src/enums/font-properties.enum';

import { commonWebsitesFormButtonTextStyles } from '@common/constants/button-sizes.const';
import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';

const justifyStyles = {
    [EHorizontalPosition.L]: 'flex-start',
    [EHorizontalPosition.M]: 'center',
    [EHorizontalPosition.R]: 'flex-end',
};

export const counterSizeStyles: Record<ESize, JssStyle> = {
    [ESize.S]: {
        width: '108px',
        height: '35px',
        padding: '10px 6px !important',
        fontSize: '13px !important',
        lineHeight: '15px !important',
    },
    [ESize.M]: {
        width: '120px',
        height: '41px',
        padding: '12px 10px !important',
        fontSize: '15px !important',
        lineHeight: '17px !important',
    },
    [ESize.L]: {
        width: '132px',
        height: '57px',
        padding: '18px 16px !important',
        fontSize: '19px !important',
        lineHeight: '21px !important',
    },
};

export const variantSizeStyles: Record<ESize, JssStyle> = {
    [ESize.S]: {
        maxWidth: '220px',
        minWidth: '100px',
        height: '35px',
        padding: '8px 10px',
        fontSize: '13px',
        lineHeight: '15px',
    },
    [ESize.M]: {
        maxWidth: '240px',
        minHWidth: '120px',
        height: '41px',
        padding: '10px 16px',
        fontSize: '15px',
        lineHeight: '17px',
    },
    [ESize.L]: {
        maxWidth: '260px',
        minHWidth: '140px',
        height: '57px',
        padding: '16px 22px',
        fontSize: '19px',
        lineHeight: '21px',
    },
};

function getFontStyles(fontStyle: EFontStyle[]): JssStyle {
    return {
        fontWeight: fontStyle?.includes(EFontStyle.B) ? 'bold' : 'initial',
        fontStyle: fontStyle?.includes(EFontStyle.I) ? 'italic' : 'initial',
        textTransform: fontStyle?.includes(EFontStyle.AT) ? 'uppercase' : 'initial',
    };
}

function getGeneralSettings(data: GeneralSettings): JssStyle {
    const generalSettings: JssStyle = {
        backgroundColor: themeColorCss(data.backgroundColor as string),
        borderRadius: `${data.borderRadius}px`,
        padding: `${data.padding}px`,
    };

    if (data.isBorder) {
        const abs = data.advancedBorderSettings;

        if (abs) {
            if (abs.isIndependentBorders) {
                generalSettings.borderTop = `${abs.borderTopWidth}px ${abs.borderTopStyle} ${abs.borderTopColor}`;
                generalSettings.borderRight = `${abs.borderRightWidth}px ${abs.borderRightStyle} ${abs.borderRightColor}`;
                generalSettings.borderBottom = `${abs.borderBottomWidth}px ${abs.borderBottomStyle} ${abs.borderBottomColor}`;
                generalSettings.borderLeft = `${abs.borderLeftWidth}px ${abs.borderLeftStyle} ${abs.borderLeftColor}`;
            } else {
                generalSettings.border = `${abs.borderTopWidth}px ${abs.borderTopStyle} ${abs.borderTopColor}`;
            }
        } else {
            generalSettings.borderWidth = `${data.borderWidth}px`;
            generalSettings.borderStyle = data.borderStyle;
            generalSettings.borderColor = themeColorCss(data.borderColor as string);
        }
    }
    // substrateColor?: string;

    return generalSettings;
}

function getGallerySettings(data: GallerySettings): JssStyle {
    return {
        margin: data.margin,

        '& img': {
            borderRadius: `${data.borderRadius}px`,
        },
    };

    // buttonColor: string;
    // changeSlide: boolean;
    // fullByClick: boolean;
}

function getInfoSettings(data: InfoSettings): JssStyle {
    const infoSettings: JssStyle = {
        backgroundColor: themeColorCss(data.backgroundColor as string),
        borderRadius: data.borderRadius,
        paddingTop: `${data.paddingTop}px`,
        paddingBottom: `${data.paddingBottom}px`,
        paddingLeft: `${data.paddingLeft}px`,
        paddingRight: `${data.paddingRight}px`,
    };

    if (data.isBorder) {
        const abs = data.advancedBorderSettings;

        if (abs) {
            if (abs.isIndependentBorders) {
                infoSettings.borderTop = `${abs.borderTopWidth}px ${abs.borderTopStyle} ${abs.borderTopColor}`;
                infoSettings.borderRight = `${abs.borderRightWidth}px ${abs.borderRightStyle} ${abs.borderRightColor}`;
                infoSettings.borderBottom = `${abs.borderBottomWidth}px ${abs.borderBottomStyle} ${abs.borderBottomColor}`;
                infoSettings.borderLeft = `${abs.borderLeftWidth}px ${abs.borderLeftStyle} ${abs.borderLeftColor}`;
            } else {
                infoSettings.border = `${abs.borderTopWidth}px ${abs.borderTopStyle} ${abs.borderTopColor}`;
            }
        } else {
            infoSettings.borderWidth = `${data.borderWidth}px`;
            infoSettings.borderStyle = data.borderStyle;
            infoSettings.borderColor = themeColorCss(data.borderColor as string);
        }
    }

    return infoSettings;
}

function commonTypographySettings(data): JssStyle {
    const fontStyles: JssStyle = getFontStyles(data.fontStyle);

    return {
        ...fontStyles,
        color: themeColorCss(data.color as string),
        fontSize: `${data.fontSize}px`,
    };
}

function getCustomButtonStyle(data: ButtonSettings): JssStyle {
    return getBaseButtonData({
        buttonBase: data.buttonBase,
        buttonHover: data.buttonHover,
        advancedRadius: data.advancedRadius,
        advancedBorderSettings: data.advancedBorderSettings,
    });
}

function getButtonShadow(data: ButtonSettings): JssStyle {
    if (!data.isShadow) {
        return {};
    }
    const s = data.shadowSettings ?? DEFAULT_SHADOW_SETTINGS;
    return {
        boxShadow: `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.spread}px ${s.color}`,
    };
}

function getHorizontalPreviewLayout(cardModalViewType: ViewModalType): JssStyle {
    if (cardModalViewType === ViewModalType.horizontal) {
        return {
            width: '70%',
            display: 'flex',
            flexDirection: 'row',
            '@media (max-width: 768px)': {
                width: '100%',
                flexDirection: 'column',
                paddingLeft: 0,
                paddingTop: '30px',
            },
            '& .left-side': {
                marginRight: '50px',
                '@media (max-width: 768px)': {
                    marginRight: 0,
                },
                '& .card-description': {
                    marginBottom: 0,
                    '@media (max-width: 768px)': {
                        marginBottom: '30px',
                    },
                },
            },
            '& .right-side': {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
                '& .card-price-wrapper': {
                    flexDirection: 'column',
                    alignItems: 'end',
                    textWrap: 'nowrap',

                    '& .card-price-block': {
                        flexDirection: 'column',
                        alignItems: 'end',
                        textWrap: 'nowrap',
                        '@media (max-width: 768px)': {
                            flexDirection: 'row',
                            alignItems: 'baseline',
                        },
                        '& .card-new-price': {
                            marginRight: 0,
                            '@media (max-width: 768px)': {
                                marginRight: '10px',
                            },
                        },
                    },
                },
            },
        };
    }
}

function getPreviewMainWrapperAling(position: EHorizontalPosition): JssStyle {
    switch (position) {
        case EHorizontalPosition.M:
            return { justifyContent: 'center' };
        case EHorizontalPosition.R:
            return { justifyContent: 'flex-end' };
        default:
            return { justifyContent: 'flex-start' };
    }
}

export function getStyles(widget: WebLegacyWidgetEntity<ProductCardWidgetData>): JssStyle {
    const {
        previewModalSettings,
        modalSettings,
        cardSettings,
        basketButton,
        cardModalViewType,
        previewHorizontalPosition,
        element,
        cardKindViewType,
        showMoreButton,
    } = widget.data;

    const { borderRadius, fullByClick, margin } = cardSettings?.gallery ?? {};

    const isMasonry = [CardKindViewType.sixth].includes(cardKindViewType);

    const blankImageWrapper: JssStyle = {
        height: '200px',
        background: '#F8FDFE',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '15px',
        justifyContent: 'center',
        '& img': {
            height: '64px',
            marginBottom: '10px',
        },
        '& span': {
            fontSize: '15px',
            color: '#023346',
            textAlign: 'center',
        },
    };

    const previewMainWrapper: JssStyle = {
        display: 'flex',
        ...getPreviewMainWrapperAling(widget.data.previewHorizontalPosition),
    };

    const previewVerticalViewWrapper: JssStyle = {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '480px',
        alignItems: justifyStyles[previewHorizontalPosition],
        ...getGeneralSettings(previewModalSettings.general),

        '& .preview-image-container': {
            width: '100%',
            '& .image-wrapper.resizeble': {
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                paddingTop: '100%',
                '& img': {
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    inset: 0,
                    objectFit: previewModalSettings?.gallery?.attachment === Attachment.cover ? 'cover' : 'contain',
                    margin: previewModalSettings?.gallery?.attachment === Attachment.cover ? '0' : 'auto',
                    userSelect: 'none',
                    borderRadius: previewModalSettings.gallery?.borderRadius + 'px',
                },
            },
            '& .image-wrapper': {
                backgroundColor: themeColorCss(previewModalSettings?.gallery?.backgroundColor as string),
                '& img': {
                    width: '100%',
                    height: 'auto',
                    borderRadius: previewModalSettings.gallery?.borderRadius + 'px',
                },
            },
            '& .blank-wrapper': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                '& img': {
                    width: '100%',
                    height: 'auto',
                },
            },
        },
    };

    const previewHorizontalViewWrapper: JssStyle = {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...getGeneralSettings(previewModalSettings.general),
        '@media (max-width: 768px)': {
            flexDirection: 'column',
        },

        '&.mbPreviewHorizontalViewWrapper': {
            flexDirection: 'column',
        },

        '& .preview-image-container': {
            width: '30%',
            '@media (max-width: 768px)': {
                width: '100%',
            },

            '&.mbPreviewImageContainer': {
                width: '100%',
            },

            '& .image-wrapper.resizeble': {
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                paddingTop: '100%',
                '& img': {
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    inset: 0,
                    objectFit: previewModalSettings?.gallery?.attachment === Attachment.cover ? 'cover' : 'contain',
                    margin: previewModalSettings?.gallery?.attachment === Attachment.cover ? '0' : 'auto',
                    userSelect: 'none',
                    borderRadius: previewModalSettings?.gallery?.borderRadius + 'px',
                },
            },
            '& .image-wrapper': {
                backgroundColor: themeColorCss(previewModalSettings?.gallery?.backgroundColor as string),
                '& img': {
                    width: '100%',
                    height: 'auto',
                    borderRadius: previewModalSettings?.gallery?.borderRadius + 'px',
                },
            },
            '& .blank-wrapper': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                '& img': {
                    width: '100%',
                    height: 'auto',
                },
            },
        },
    };

    const previewContentWrapper: JssStyle = {
        width: '100%',
        ...getInfoSettings(previewModalSettings.info),
        ...getHorizontalPreviewLayout(cardModalViewType),

        '& .card-title': {
            '& h2': {
                ...commonTypographySettings(previewModalSettings.typography.titleSettings),
                marginBottom: '10px',
            },
            marginBottom: '15px',
            '& .card-article': {
                ...commonTypographySettings(previewModalSettings.typography.descriptionSettings),
                opacity: '0.7',
                fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
            },
        },

        '& .card-description': {
            ...commonTypographySettings(previewModalSettings.typography.descriptionSettings),
            marginBottom: '30px',
            '& p': {
                ...commonTypographySettings(previewModalSettings.typography.descriptionSettings),
            },
            '& div': {
                ...commonTypographySettings(previewModalSettings.typography.descriptionSettings),
                '& p': {
                    ...commonTypographySettings(previewModalSettings.typography.descriptionSettings),
                },
            },
        },

        '& .card-old-price': {
            ...commonTypographySettings(previewModalSettings.typography.oldPriceSettings),
            textDecoration: 'line-through',
            fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
        },

        '& .card-price-wrapper': {
            marginBottom: '15px',

            '& .card-price-block': {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'baseline',
                '& .card-new-price': {
                    ...commonTypographySettings(previewModalSettings.typography.priceSettings),
                    marginRight: '10px',
                    lineHeight: previewModalSettings.typography.priceSettings.fontSize + 'px',
                },

                '& .card-old-price': {
                    ...commonTypographySettings(previewModalSettings.typography.oldPriceSettings),
                    textDecoration: 'line-through',
                    lineHeight: cardSettings.typography.priceSettings.fontSize + 'px',
                },
            },
        },

        '& .buttonWrapper': {
            display: 'flex',
            margin: '-10px',
            '& .card-button': {
                padding: commonWebsitesFormButtonTextStyles[showMoreButton.size || ESize.M].padding,
                fontSize: commonWebsitesFormButtonTextStyles[showMoreButton.size || ESize.M].fontSize,
                lineHeight: commonWebsitesFormButtonTextStyles[showMoreButton.size || ESize.M].lineHeight,
            },
        },
    };

    const gridContainerStyles = {
        display: 'grid',
        gap: margin,
        transition: 'gap 0.25s ease-out',
        gridTemplateColumns: `repeat(2, 1fr)`,
        gridAutoRows: '1fr',
        '@media (max-width: 992px)': {
            gap: margin / 2,
        },
    };

    const masonryContainerStyles = {
        display: 'flex',
        columnGap: margin,
        transition: 'gap 0.25s ease-out',
        rowGap: 'unset',
        margin: '-5px',
        '& > div': {
            display: 'flex',
            flexDirection: 'column',
            flexBasis: '50%',
            rowGap: margin,
            transition: 'gap 0.25s ease-out',
        },
        '& .gridItem': {
            margin: '5px',
        },
    };

    const openViewWrapper: JssStyle = {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        ...getGeneralSettings(cardSettings.general),
        '@media (max-width: 768px)': {
            flexDirection: 'column',
        },

        '&.mbViewWrapper': {
            flexDirection: 'column',
        },

        '& .imageContainer': {
            width: cardSettings?.general?.galleryWidth,
            ...getGallerySettings(cardSettings?.gallery),
            '@media (max-width: 768px)': {
                width: '100%',
                minHeight: 'auto !important',
            },

            '&.mbImageContainer': {
                width: '100%',
                minHeight: 'auto !important',
            },

            '& .gridContainer': isMasonry ? masonryContainerStyles : gridContainerStyles,

            '& .gridItem': {
                extend: {},
                position: 'relative',
            },
            '& .blank-wrapper': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                '& img': {
                    width: '100%',
                    height: 'auto',
                },
            },
            '& .default-image': {
                width: '100%',
                height: 'auto',
            },
            '& .gridImage': element?.images?.length
                ? {
                      width: '100%',
                      height: '100%',
                      position: 'absolute',
                      inset: 0,
                      objectFit: cardSettings?.gallery?.attachment === Attachment.cover ? 'cover' : 'contain',
                      margin: cardSettings?.gallery?.attachment === Attachment.cover ? '0' : 'auto',
                      userSelect: 'none',
                  }
                : { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
            '& .imageWrapper.resizeble': {
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                borderRadius,
                paddingTop: '100%',
                cursor: fullByClick ? 'pointer' : 'default',
            },
            '& .thumbWrapper': {
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                borderRadius,
                backgroundColor: themeColorCss(cardSettings?.gallery?.backgroundColor as string),
            },
            '& .thumbImage': {
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                userSelect: 'none',
            },
            '& .vertical-image-wrapper': {
                // maxHeight: '100vh',
                // height: '100%',
                // overflowX: 'hidden',
                // overflowY: 'auto',
                width: '100%',
                '& .default-image': {
                    marginBottom: '10px',
                },
                '& div:last-child': {
                    '& .default-image': {
                        marginBottom: 0,
                    },
                },
                '@media (max-width: 768px)': {
                    maxHeight: '100%',
                    minHeight: 'auto !important',
                    position: 'relative !important',
                },
            },
            '& .thumbs-swiper-container': {
                '& .thumbsSwiper': {
                    '& .swiper-slide': {
                        filter: 'brightness(0.8)',
                        height: '80px !important',
                        width: '80px !important',
                        margin: '10px 10px 0 0',
                        '&:hover': {
                            cursor: 'pointer',
                        },
                    },
                    '& .swiper-slide-thumb-active': {
                        filter: 'brightness(1)',
                        border: '3px solid',
                        borderColor: themeColorCss(cardSettings?.gallery?.paginationColor as string),
                    },
                },
            },
            '& .image-wrapper': {
                backgroundColor: themeColorCss(cardSettings?.gallery?.backgroundColor as string),
            },
            '& .swiper-button-prev, .swiper-button-next': {
                top: 'calc(50% - 20px)',
                width: '40px',
                height: '40px',
                padding: '10px',
                borderRadius: '50%',
                backgroundColor: themeColorCss(cardSettings?.gallery?.buttonBackground as string),
                boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.3)',
                '&:after': {
                    fontSize: '22px',
                },
                '&:hover': {
                    opacity: 0.8,
                },

                '@media (max-width: 992px)': {
                    display: 'none',
                },
            },
            '& .swiper-button-prev': {
                '&:after': {
                    marginRight: '3px',
                },
            },
            '& .swiper-button-next': {
                '&:after': {
                    marginLeft: '3px',
                },
            },
        },
        '& .contentContainer': {
            width: cardSettings?.general?.contentWidth,
            '& .contentWrapper': {
                ...getInfoSettings(cardSettings.info),
                '@media (max-width: 768px)': {
                    width: '100%',
                    padding: '30px 0 0 0',
                    position: 'relative !important',
                    top: 'auto !important',
                },

                '&.mbContentWrapper': {
                    width: '100%',
                    padding: '30px 0 0 0',
                    position: 'relative !important',
                    top: 'auto !important',
                },

                '& .card-title': {
                    '& h2': {
                        ...commonTypographySettings(cardSettings.typography.titleSettings),
                        marginBottom: '10px',
                    },
                    marginBottom: '30px',
                    '& .card-article': {
                        ...commonTypographySettings(cardSettings.typography.descriptionSettings),
                        opacity: '0.7',
                        fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
                    },
                },

                '& .card-price-wrapper': {
                    marginBottom: '30px',

                    '& .card-price-block': {
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'baseline',
                        flexWrap: 'wrap',
                        '& .card-new-price': {
                            ...commonTypographySettings(cardSettings.typography.priceSettings),
                            marginRight: '10px',
                            lineHeight: cardSettings.typography.priceSettings.fontSize + 'px',
                        },

                        '& .card-old-price': {
                            ...commonTypographySettings(cardSettings.typography.oldPriceSettings),
                            textDecoration: 'line-through',
                            lineHeight: cardSettings.typography.priceSettings.fontSize + 'px',
                        },
                    },
                },

                '& .card-description': {
                    ...commonTypographySettings(cardSettings.typography.descriptionSettings),
                    marginBottom: '30px',
                    '& p': {
                        ...commonTypographySettings(cardSettings.typography.descriptionSettings),
                    },
                    '& div': {
                        ...commonTypographySettings(cardSettings.typography.descriptionSettings),
                        '& p': {
                            ...commonTypographySettings(cardSettings.typography.descriptionSettings),
                        },
                    },
                },

                '& .buttonWrapper': {
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '30px',
                    margin: '-10px',
                    flexWrap: 'wrap',
                    '& .card-button': {
                        padding: commonWebsitesFormButtonTextStyles[basketButton.size || ESize.M].padding,
                        fontSize: commonWebsitesFormButtonTextStyles[basketButton.size || ESize.M].fontSize,
                        lineHeight: commonWebsitesFormButtonTextStyles[basketButton.size || ESize.M].lineHeight,
                    },
                    '& .card-product-counter': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexDirection: 'row',
                        borderWidth: '1px',
                        boxSizing: 'border-box',
                        borderStyle: 'solid',
                        whiteSpace: 'nowrap',
                        borderTopWidth: `${
                            cardSettings.productVariants.inputBorderType === InputBorderEnum.BOTTOM_BORDER
                                ? '0px'
                                : '1px'
                        }`,
                        borderRightWidth: `${
                            cardSettings.productVariants.inputBorderType === InputBorderEnum.BOTTOM_BORDER
                                ? '0px'
                                : '1px'
                        }`,
                        borderLeftWidth: `${
                            cardSettings.productVariants.inputBorderType === InputBorderEnum.BOTTOM_BORDER
                                ? '0px'
                                : '1px'
                        }`,
                        borderRadius: `${cardSettings.productVariants.borderRadius}px`,
                        background: themeColorCss(cardSettings.productVariants.backgroundColor as string),
                        borderColor: themeColorCss(cardSettings.productVariants.borderColor as string),
                        color: themeColorCss(cardSettings.productVariants.inputTextColor as string),
                        margin: '10px',
                        ...counterSizeStyles[cardSettings.productVariants.inputSize || ESize.M],
                        padding: '0 !important',
                        '& .counter-control': {
                            cursor: 'pointer',
                            padding: counterSizeStyles[cardSettings.productVariants.inputSize || ESize.M].padding,
                            background: 'transparent',
                            border: 'none',
                            color: themeColorCss(cardSettings.productVariants.inputTextColor as string),
                            '&:hover': {
                                opacity: 0.8,
                            },
                        },
                        '& .quantity-control': {
                            background: 'transparent',
                            color: themeColorCss(cardSettings.productVariants.inputTextColor as string),
                            fontSize: counterSizeStyles[cardSettings.productVariants.inputSize || ESize.M].fontSize,
                            lineHeight: counterSizeStyles[cardSettings.productVariants.inputSize || ESize.M].lineHeight,
                        },
                        '& .counter': {
                            color: themeColorCss(cardSettings.productVariants.inputTextColor as string),
                            background: themeColorCss(cardSettings.productVariants.backgroundColor as string),
                            padding: counterSizeStyles[cardSettings.productVariants.inputSize || ESize.M].padding,
                        },
                        '& .disabled': {
                            cursor: 'not-allowed',
                            pointerEvents: 'none',
                            opacity: 0.5,
                        },
                    },
                    '& .disabled': {
                        cursor: 'not-allowed',
                        pointerEvents: 'none',
                        opacity: 0.5,
                    },
                    '@media(max-width: 992px)': {
                        flexWrap: 'wrap-reverse',
                        '& .card-button': {
                            width: '100%',
                        },
                        '& .card-product-counter': {
                            width: '50%',
                            '& .counter-control': {
                                // padding: '10px',
                                // margin: '-10px',
                                '& svg': {
                                    width: '12px',
                                    height: '12px',
                                },
                            },
                        },
                    },
                },

                '& .card-parameters': {
                    marginBottom: '20px',
                    '& label': {
                        display: 'block',
                        fontWeight: 'bold',
                        fontSize: cardSettings.typography.descriptionSettings.fontSize + 2 + 'px',
                        color: themeColorCss(cardSettings.typography.descriptionSettings.color as string),
                        fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
                        marginBottom: '10px',
                    },
                    '& p': {
                        ...commonTypographySettings(cardSettings.typography.descriptionSettings),
                        marginBottom: '5px',
                        '& .param-prop': {
                            marginRight: '5px',
                        },
                        '& .param-value': {
                            opacity: 0.7,
                        },
                    },
                    '&:last-child': {
                        marginBottom: 0,
                    },
                },

                '& .variants-block': {
                    flexWrap: 'wrap',
                    marginBottom: '20px',

                    '& label': {
                        display: cardSettings.productVariants.showLabels ? 'block' : 'none',
                        fontWeight: 'bold',
                        fontSize: cardSettings.typography.descriptionSettings.fontSize + 'px',
                        color: themeColorCss(cardSettings.productVariants.textColor as string),
                        fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
                        marginBottom: `${
                            cardSettings.productVariants.inputSize === ESize.S
                                ? '6px'
                                : cardSettings.productVariants.inputSize === ESize.M
                                  ? '8px'
                                  : '10px'
                        }`,
                    },

                    '& select': {
                        ...variantSizeStyles[cardSettings.productVariants.inputSize || ESize.M],
                        marginBottom: `${
                            cardSettings.productVariants.inputSize === ESize.S
                                ? '10px'
                                : cardSettings.productVariants.inputSize === ESize.M
                                  ? '12px'
                                  : '15px'
                        }`,
                        '&:hover': {
                            borderColor: '#9a9a9a',
                        },
                        '&:focus': {
                            borderColor: '#82f6ff',
                            boxShadow: '0 0 0 2px rgb(151 247 255 / 30%);',
                            outline: '0',
                        },
                        marginRight: '15px',
                        borderWidth: '1px',
                        boxSizing: 'border-box',
                        borderStyle: 'solid',
                        whiteSpace: 'nowrap',
                        borderTopWidth: `${
                            cardSettings.productVariants.inputBorderType === InputBorderEnum.BOTTOM_BORDER
                                ? '0px'
                                : '1px'
                        }`,
                        borderRightWidth: `${
                            cardSettings.productVariants.inputBorderType === InputBorderEnum.BOTTOM_BORDER
                                ? '0px'
                                : '1px'
                        }`,
                        borderLeftWidth: `${
                            cardSettings.productVariants.inputBorderType === InputBorderEnum.BOTTOM_BORDER
                                ? '0px'
                                : '1px'
                        }`,
                        borderRadius: `${cardSettings.productVariants.borderRadius}px`,
                        background: themeColorCss(cardSettings.productVariants.backgroundColor as string),
                        borderColor: themeColorCss(cardSettings.productVariants.borderColor as string),
                        color: themeColorCss(cardSettings.productVariants.inputTextColor as string),
                        '& option': {
                            background: themeColorCss(cardSettings.productVariants.backgroundColor as string),
                            '&[disabled]': {
                                opacity: 0.5,
                                background: '#e7e7e7',
                            },
                        },
                    },
                },
            },
            '@media (max-width: 768px)': {
                minHeight: 'auto !important',
                width: '100%',
            },

            '&.mbContentContainer': {
                minHeight: 'auto !important',
                width: '100%',
            },
        },
    };

    const modalContainerStyles: JssStyle = {
        width: '100%',
        maxWidth: '900px',
        margin: '10px auto',

        '@media (max-width: 768px)': {
            margin: 0,
        },
        '& .modal-content': {
            maxWidth: '900px',
        },
    };

    const modalWrapper: JssStyle = {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        position: 'relative',
        ...getGeneralSettings(modalSettings.general),
        '@media (max-width: 600px)': {
            flexDirection: 'column',
        },

        '& .close-modal-btn': {
            position: 'absolute',
            top: '10px',
            right: '10px',
            width: '24px',
            height: '24px',
            fontSize: '24px',
            lineHeight: '24px',
            borderRadius: '50%',
            padding: 0,
            border: 0,
            backgroundColor: '#f3f3f3',
            color: '#555',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: '#ebebeb',
            },
        },

        '& .imageContainer': {
            width: 'calc(40% - 30px)',
            marginRight: '30px',
            ...getGallerySettings(cardSettings?.gallery),
            '@media (max-width: 600px)': {
                width: '100%',
                marginRight: 0,
                marginBottom: '30px',
            },

            '& .blank-wrapper': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                '& img': {
                    width: '100%',
                    height: 'auto',
                },
            },

            '& .default-image': {
                width: '100%',
                height: 'auto',
            },

            '& .gridImage': element?.images?.length
                ? {
                      width: '100%',
                      height: '100%',
                      position: 'absolute',
                      inset: 0,
                      objectFit: cardSettings?.gallery?.attachment === Attachment.cover ? 'cover' : 'contain',
                      margin: cardSettings?.gallery?.attachment === Attachment.cover ? '0' : 'auto',
                      userSelect: 'none',
                  }
                : { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },

            '& .image-wrapper': {
                backgroundColor: themeColorCss(cardSettings?.gallery?.backgroundColor as string),
                paddingTop: '120%',
            },
            '& .imageWrapper.resizeble': {
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                borderRadius,
                paddingTop: '100%',
                cursor: fullByClick ? 'pointer' : 'default',
            },
            '& .swiper-button-prev, .swiper-button-next': {
                top: 'calc(50% - 20px)',
                width: '40px',
                height: '40px',
                padding: '10px',
                borderRadius: '50%',
                backgroundColor: themeColorCss(cardSettings?.gallery?.buttonBackground as string),
                boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.3)',
                '&:after': {
                    fontSize: '22px',
                },
                '&:hover': {
                    opacity: 0.8,
                },

                '@media (max-width: 992px)': {
                    display: 'none',
                },
            },
            '& .swiper-button-prev': {
                '&:after': {
                    marginRight: '3px',
                },
            },
            '& .swiper-button-next': {
                '&:after': {
                    marginLeft: '3px',
                },
            },

            '& img': {
                width: '100%',
                height: 'auto',
                borderRadius,
            },
        },

        '& .contentWrapper': {
            width: '60%',
            '@media (max-width: 600px)': {
                width: '100%',
            },

            '& .card-title': {
                '& h2': {
                    ...commonTypographySettings(modalSettings.typography.titleSettings),
                    marginBottom: '10px',
                },
                marginBottom: '30px',
                '& .card-article': {
                    ...commonTypographySettings(modalSettings.typography.descriptionSettings),
                    opacity: '0.7',
                    fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
                },
            },

            '& .card-price-wrapper': {
                marginBottom: '30px',

                '& .card-price-block': {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'baseline',
                    '& .card-new-price': {
                        ...commonTypographySettings(modalSettings.typography.priceSettings),
                        marginRight: '10px',
                        lineHeight: modalSettings.typography.priceSettings.fontSize + 'px',
                    },

                    '& .card-old-price': {
                        ...commonTypographySettings(modalSettings.typography.oldPriceSettings),
                        textDecoration: 'line-through',
                        lineHeight: modalSettings.typography.priceSettings.fontSize + 'px',
                    },
                },
            },

            '& .card-description': {
                ...commonTypographySettings(modalSettings.typography.descriptionSettings),
                marginBottom: '30px',
                '& p': {
                    ...commonTypographySettings(cardSettings.typography.descriptionSettings),
                },
                '& div': {
                    ...commonTypographySettings(cardSettings.typography.descriptionSettings),
                    '& p': {
                        ...commonTypographySettings(cardSettings.typography.descriptionSettings),
                    },
                },
            },

            '& .buttonWrapper': {
                display: 'flex',
                alignItems: 'center',
                marginBottom: '30px',
                margin: '-10px',
                '& .card-button': {
                    padding: commonWebsitesFormButtonTextStyles[basketButton.size || ESize.M].padding,
                    fontSize: commonWebsitesFormButtonTextStyles[basketButton.size || ESize.M].fontSize,
                    lineHeight: commonWebsitesFormButtonTextStyles[basketButton.size || ESize.M].lineHeight,
                },
                '& .card-product-counter': {
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                    borderWidth: '1px',
                    boxSizing: 'border-box',
                    borderStyle: 'solid',
                    whiteSpace: 'nowrap',
                    borderTopWidth: `${
                        modalSettings.productVariants.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'
                    }`,
                    borderRightWidth: `${
                        modalSettings.productVariants.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'
                    }`,
                    borderLeftWidth: `${
                        modalSettings.productVariants.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'
                    }`,
                    borderRadius: `${modalSettings.productVariants.borderRadius}px`,
                    background: themeColorCss(modalSettings.productVariants.backgroundColor as string),
                    borderColor: themeColorCss(modalSettings.productVariants.borderColor as string),
                    color: themeColorCss(modalSettings.productVariants.inputTextColor as string),
                    margin: '10px',
                    ...counterSizeStyles[modalSettings.productVariants.inputSize || ESize.M],
                    padding: '0 !important',
                    '& .counter-control': {
                        cursor: 'pointer',
                        padding: counterSizeStyles[modalSettings.productVariants.inputSize || ESize.M].padding,
                        background: 'transparent',
                        border: 'none',
                        color: themeColorCss(modalSettings.productVariants.inputTextColor as string),
                        '&:hover': {
                            opacity: 0.8,
                        },
                    },
                    '& .quantity-control': {
                        background: 'transparent',
                        color: themeColorCss(modalSettings.productVariants.inputTextColor as string),
                        fontSize: counterSizeStyles[modalSettings.productVariants.inputSize || ESize.M].fontSize,
                        lineHeight: counterSizeStyles[modalSettings.productVariants.inputSize || ESize.M].lineHeight,
                    },
                    '& .counter': {
                        color: themeColorCss(modalSettings.productVariants.inputTextColor as string),
                        background: themeColorCss(modalSettings.productVariants.backgroundColor as string),
                        padding: counterSizeStyles[modalSettings.productVariants.inputSize || ESize.M].padding,
                    },
                    '& .disabled': {
                        cursor: 'not-allowed',
                        pointerEvents: 'none',
                        opacity: 0.5,
                    },
                },
                '& .disabled': {
                    cursor: 'not-allowed',
                    pointerEvents: 'none',
                    opacity: 0.5,
                },
                '@media(max-width: 992px)': {
                    flexWrap: 'wrap-reverse',
                    '& .card-button': {
                        width: '100%',
                    },
                    '& .card-product-counter': {
                        width: '50%',
                        '& .counter-control': {
                            '& svg': {
                                width: '12px',
                                height: '12px',
                            },
                        },
                    },
                },
            },

            '& .card-parameters': {
                marginBottom: '20px',
                '& label': {
                    display: 'block',
                    fontWeight: 'bold',
                    fontSize: modalSettings.typography.descriptionSettings.fontSize + 2 + 'px',
                    color: themeColorCss(modalSettings.typography.descriptionSettings.color as string),
                    fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
                    marginBottom: '10px',
                },
                '& p': {
                    ...commonTypographySettings(modalSettings.typography.descriptionSettings),
                    marginBottom: '5px',
                    '& .param-prop': {
                        marginRight: '5px',
                    },
                    '& .param-value': {
                        opacity: 0.7,
                    },
                },
                '&:last-child': {
                    marginBottom: 0,
                },
            },

            '& .variants-block': {
                marginBottom: '20px',

                '& label': {
                    display: modalSettings.productVariants.showLabels ? 'block' : 'none',
                    fontWeight: 'bold',
                    fontSize: modalSettings.typography.descriptionSettings.fontSize + 2 + 'px',
                    color: themeColorCss(modalSettings.productVariants.textColor as string),
                    fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
                    marginBottom: '10px',
                },

                '& select': {
                    ...variantSizeStyles[modalSettings.productVariants.inputSize || ESize.M],
                    '&:hover': {
                        borderColor: '#9a9a9a',
                    },
                    '&:focus': {
                        borderColor: '#82f6ff',
                        boxShadow: '0 0 0 2px rgb(151 247 255 / 30%);',
                        outline: '0',
                    },
                    marginRight: '30px',
                    borderWidth: '1px',
                    boxSizing: 'border-box',
                    borderStyle: 'solid',
                    whiteSpace: 'nowrap',
                    borderTopWidth: `${
                        modalSettings.productVariants.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'
                    }`,
                    borderRightWidth: `${
                        modalSettings.productVariants.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'
                    }`,
                    borderLeftWidth: `${
                        modalSettings.productVariants.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'
                    }`,
                    borderRadius: `${modalSettings.productVariants.borderRadius}px`,
                    background: themeColorCss(modalSettings.productVariants.backgroundColor as string),
                    borderColor: themeColorCss(modalSettings.productVariants.borderColor as string),
                    color: themeColorCss(modalSettings.productVariants.inputTextColor as string),
                    '& option': {
                        background: themeColorCss(modalSettings.productVariants.backgroundColor as string),
                        '&[disabled]': {
                            opacity: 0.5,
                            background: '#e7e7e7',
                        },
                    },
                },
            },
        },
    };

    const fullDescription: JssStyle = {
        ...commonTypographySettings(cardSettings.typography.descriptionSettings),
        marginTop: '30px',
        '& p': {
            ...commonTypographySettings(cardSettings.typography.descriptionSettings),
        },
        '& div': {
            ...commonTypographySettings(cardSettings.typography.descriptionSettings),
            '& p': {
                ...commonTypographySettings(cardSettings.typography.descriptionSettings),
            },
        },
    };

    const buttonStyles: JssStyle = {
        ...(basketButton.styleType === EButtonStyleType.custom ? getCustomButtonStyle(basketButton) : {}),
    };

    const showMoreButtonStyle: JssStyle = {
        ...(showMoreButton.styleType === EButtonStyleType.custom ? getCustomButtonStyle(showMoreButton) : {}),
    };

    const basketButtonShadow: JssStyle = getButtonShadow(basketButton);

    const showMoreButtonShadow: JssStyle = getButtonShadow(showMoreButton);

    return {
        blankImageWrapper,
        openViewWrapper,
        previewMainWrapper,
        previewVerticalViewWrapper,
        previewContentWrapper,
        previewHorizontalViewWrapper,
        modalContainerStyles,
        modalWrapper,
        fullDescription,
        buttonStyles,
        showMoreButtonStyle,
        basketButtonShadow,
        showMoreButtonShadow,
    };
}
